import Backbone from 'backbone';
import Promise from 'bluebird';
import _ from 'underscore';
import pgrid from 'projection-grid';
import { paginationPlugin } from '@bingads-webui/projection-grid-pagination';
import { a11yPlugin } from '@bingads-webui/projection-grid-a11y';
import { cloneDeep, get } from '@bingads-webui-universal/primitive-utilities';
import { DependencyResolver } from '@bingads-webui-campaign/dependency-resolver';
import { ageOptions, genderOptions, deviceOptions } from '@bingads-webui-campaign/audience-campaign-shared';
import { GridConfigGenerator } from './grid-config-generator';
import {
  computeBidAdjustment,
  isBidAdjustmentValid,
  getValidationError,
  getRealCashbackPercentValue,
} from './bid-adjustment-utils';
import template from './bid-adjustment-template.pug';

const criterionsWithPagination = [
  'Location',
  'NegativeLocation',
  'Radius',
  'CompanyName',
  'Industry',
  'JobFunction',
  'Audience',
];

let permissions = {};

export class BidAdjustment extends Backbone.View {
  initialize(options = {}) {
    this.decimal = DependencyResolver.get('component/humanize/decimal');
    this.i18n = DependencyResolver.get('component/i18n/loader!campaignui/feature/audience-campaign');
    this.targetConstants = DependencyResolver.get('campaignui/feature/base-targets/viewmodel/targetConstants');
    this.BaseTargetUpdateView = DependencyResolver.get('campaignui/feature/base-targets/view/baseUpdate');
    this.targetTypes = DependencyResolver.get('campaignui/feature/base-targets/viewmodel/targetTypes');
    this.currentLevelTargetInfo = options.currentLevelTargetInfo;
    this.parentLevelTargetInfo = options.parentLevelTargetInfo || {};
    this.viewContext = options.viewContext || {};
    this.isAudienceCampaign = _.has(options, 'isAudienceCampaign') ? options.isAudienceCampaign : true;
    this.readonly = !!options.readonly;
    this.showExclusion = _.has(options, 'showExclusion') ? options.showExclusion : true;
    this.activityLogger = options.activityLogger;
    this.campaignId = options.campaignId || '';
    this.adgroupId = options.adgroupId || '';
    this.level = options.level || '';
    this.scenario = options.scenario || '';
    this.supportCashbackSettings = options.supportCashbackSettings;
    this.isSearchDSAShoppingAdgroupLevel = options.isSearchDSAShoppingAdgroupLevel;
    // Set inital state to valid
    this.isValid = true;
    this.isBidAdjustmentValid = true;
    this.onChange = options.onChange;

    const permissionConfig = DependencyResolver.get('component/config/index');
    permissions = permissionConfig.get('Permissions');

    this.initializeWithTypes(options.criterionTypes);
  }

  initializeWithTypes(criterionTypes) {
    this.criterionTypes = criterionTypes;
    this.criterionType = _.first(criterionTypes);

    _.each(criterionTypes, (criterionType) => {
      this.listenTo(
        this.currentLevelTargetInfo,
        `${this.currentLevelTargetInfo.resourceName}${criterionType}Changed`,
        () => this.onCriterionChanged()
      );
    });

    this.shouldShowPagination = false;

    if (_.intersection(criterionTypes, criterionsWithPagination).length > 0) {
      this.shouldShowPagination = true;
    }
  }

  /**
    * Get current state of control
    *
    * @returns {Object} State current state
    */
  getState() {
    return {
      isValid: this.isBidAdjustmentValid &&
        (this.isCashbackPercentValid || !this.supportCashbackSettings),
    };
  }

  onCriterionChanged() {
    const currentCriterions = this.fetchCriterions();

    if (this.grid && this.grid.gridView) {
      this.grid.gridView.patch({
        dataSource: {
          data: currentCriterions,
        },
      });
    }
  }

  fetchSingleCriterionType(criterionType, targetInfo) {
    let criterions = cloneDeep(targetInfo.getAllByType(criterionType));

    const typeToOptions = {
      Age: ageOptions,
      Gender: genderOptions,
      Device: deviceOptions,
    };

    const typeToPath = {
      Age: 'Criterion.AgeCriterion.Target',
      Gender: 'Criterion.GenderCriterion.Target',
      Device: 'Criterion.DeviceCriterion.DeviceName',
    };

    if (!this.isAudienceCampaign && _.contains(_.keys(typeToOptions), criterionType)) {
      const orderedNames = _.keys(typeToOptions[criterionType]);

      let orderedCriterions = [];
      _.each(orderedNames, (name) => {
        orderedCriterions = orderedCriterions.concat(_.filter(criterions, criterion =>
          get(criterion, typeToPath[criterionType]) === name));
      });

      criterions = _.compact(orderedCriterions);
    }

    return criterions;
  }

  fetchCriterions(isCurrent = true) {
    if (this.criterionTypes) {
      let currentCriterions = [];

      _.each(this.criterionTypes, (criterionType) => {
        const criterions = this.fetchSingleCriterionType(
          criterionType,
          isCurrent ? this.currentLevelTargetInfo : this.parentLevelTargetInfo
        );
        currentCriterions = currentCriterions.concat(criterions);
      });

      return currentCriterions;
    }

    return [];
  }

  /**
    * Event handler for bid adjustment change
    * @private
    * @param {Object} e change event
    *
    * @returns {Promise} - Promise of handling bid adjustment change
    */
  handleBidAdjustmentChange(e) {
    const bidAdjustmentControl = this.grid.gridView.$(e.target).parent();

    const bidAdjPercentage = bidAdjustmentControl.children('input').val();
    const bidAdjOption = bidAdjustmentControl.children('select').val();

    bidAdjustmentControl.children('.error').empty();
    bidAdjustmentControl.children('.error').removeAttr('role');
    bidAdjustmentControl.children('.error').attr('aria-hidden', 'true');
    this.isBidAdjustmentValid = true;

    // Validate bid adjustment
    const bidAdj = computeBidAdjustment(bidAdjPercentage, bidAdjOption);
    if (!isBidAdjustmentValid(bidAdj, this.criterionType)) {
      bidAdjustmentControl.children('.error').attr('role', 'alert');
      bidAdjustmentControl.children('.error').removeAttr('aria-hidden');
      bidAdjustmentControl.children('.error').text(getValidationError(bidAdj, this.criterionType, (this.isSearchDSAShoppingAdgroupLevel || (this.isAudienceCampaign && this.level === 'adgroup'))));
      this.isBidAdjustmentValid = false;

      if (this.onChange) {
        this.onChange(this.isBidAdjustmentValid);
      }
      return Promise.resolve();
    }

    // Update grid item data with the BidAdjustment value
    const rowData = this.grid.gridView.itemOfElement(e.target);
    if (!rowData) {
      return Promise.resolve(false);
    }
    if (rowData.Criterion.Type === 'Audience') {
      rowData.Criterion.BidMultiplier = bidAdj;
    } else {
      rowData.Criterion.BidAdjustment = bidAdj;
    }

    if (this.activityLogger) {
      this.activityLogger.signal(`bid adjustment change: ${this.criterionType}`, 'bid adjustment');
    }

    return this.currentLevelTargetInfo.updateAsync(rowData);
  }

  /**
   * Currently the implemenation of bid adj. column is really ridiculous
   * if the input number is validated, the control will be completely unmounted
   * then re-created again which is caused by calling
   * this.currentLevelTargetInfo.updateAsync(rowData).
   * To avoid this behavior for cashback percent column, I have to implement
   * a different validation by checking the error spans and try not to trigger
   * update event.
   */
  get isCashbackPercentValid() {
    const $cashbackPercentErrors = this.$el.find('.bid-adjustment-grid td[data-Column=CashbackPercent] .error');
    return _.every($cashbackPercentErrors, (__, i) => {
      const $err = $cashbackPercentErrors.eq(i);
      return $err.text() === '';
    });
  }

  handleCashbackPercentChange(validated, cashbackPercent, row) {
    const rowData = this.grid.gridView.itemOfElement(row);
    if (validated) {
      if (!rowData) {
        return;
      }
      rowData.Criterion.CashbackPercent =
        getRealCashbackPercentValue(cashbackPercent, this.decimal);

      if (this.activityLogger) {
        this.activityLogger.signal(`cashback percent change: ${this.criterionType}`, 'cashback percent');
      }

      this.currentLevelTargetInfo.updateAsync(rowData, true);
    }
  }

  /**
   * Renders the view
   *
   * @returns {View} returns the view itself
   */
  render() {
    this.$el.html(template(this));

    this.initializeAndRenderGrid();

    return this;
  }

  getDeviceTypeOptions(campaignBids = {}, adgroupBids = {}) {
    const deviceKeys = this.targetConstants.rowKeys.Device.concat(['Computers']);

    if (permissions.IsOriginalDeviceTargetingEnabled) {
      _.each(deviceKeys, (device) => {
        _.each(this.targetConstants.osNames[device], (os) => {
          deviceKeys.push(`${device}${os}`);
        });
      });
    }

    const deviceTypeOptions = {};

    _.each(deviceKeys, (device) => {
      deviceTypeOptions[`DeviceType${device}CampaignBidAdjustment`] = campaignBids[`Device${device}Bid`] || 0;
      deviceTypeOptions[`DeviceType${device}CampaignBidId`] = campaignBids[`Device${device}BidId`] || '';
      deviceTypeOptions[`DeviceType${device}AdgroupBidAdjustment`] = adgroupBids[`Device${device}Bid`] || 0;
      deviceTypeOptions[`DeviceType${device}AdgroupBidId`] = adgroupBids[`Device${device}BidId`] || '';
    });
    return deviceTypeOptions;
  }

  extractBids(targets) {
    const bids = {};

    _.each(targets, (target) => {
      let prefix = `${target.Criterion.Type}${target.Criterion.DeviceCriterion[this.targetConstants.targetTypeToFilterProperty[target.Criterion.Type]]}`;

      if (target.Criterion.Type === 'Device' && target.Criterion.OSName) {
        prefix += target.Criterion.OSName;
      }
      const name = `${prefix}Bid`;
      const id = `${name}Id`;

      bids[name] = target.Criterion.BidAdjustment ? target.Criterion.BidAdjustment : 0;
      bids[id] = target.Id || '';
    });
    return bids;
  }

  /**
    * Initializes and renders the grid
    * @private
    *
    * @returns {undefined}
    */
  initializeAndRenderGrid() {
    const currentCriterions = this.fetchCriterions();

    // When opening both device os targeting
    // and linkedInTargeting pilots, there should be
    // both features showing up.
    if (this.criterionType === 'Device' &&
      permissions.IsOriginalDeviceTargetingEnabled
    ) {
      let campaignBids = {};
      let adgroupBids = {};
      let isParentView = false;
      if (this.level === 'campaign' || this.scenario === 'adgroup-create') {
        campaignBids = this.extractBids(currentCriterions);
      } else if (this.level === 'adgroup') {
        if (Object.keys(this.parentLevelTargetInfo).length !== 0) {
          const parentCriterions = this.fetchCriterions(false);
          campaignBids = this.extractBids(parentCriterions);
        } else {
          isParentView = true;
        }
        adgroupBids = this.extractBids(currentCriterions);
      }
      const deviceTypeOptions = this.getDeviceTypeOptions(campaignBids, adgroupBids);
      this.deviceTargetsUpdateView = new this.BaseTargetUpdateView(_.extend({
        targetType: this.targetTypes.OriginalDevice,
        campaignId: this.campaignId,
        adgroupId: this.adgroupId,
        level: this.level,
        scenario: this.scenario,
        activityLogger: this.activityLogger,
        showLevelToggle: false,
        isParentView,
        onChange: this.onChange,
      }, deviceTypeOptions));
      this.$('.bid-adjustment-grid').append(this.deviceTargetsUpdateView.render().el);
    } else {
      const configGenerator = new GridConfigGenerator({
        data: currentCriterions,
        criterionType: this.criterionType,
        readonly: this.readonly,
        showExclusion: this.showExclusion,
        activityLogger: this.activityLogger,
        supportCashbackSettings: this.supportCashbackSettings,
        viewContext: this.viewContext,
        onCashbackPercentChange: this.handleCashbackPercentChange.bind(this),
        $gridRoot: this.$('.bid-adjustment-grid'),
      });
      const config = configGenerator.getConfig();

      const gridFactory = pgrid.factory({ vnext: true });

      if (this.shouldShowPagination) {
        gridFactory
          .use(paginationPlugin)
          .use(a11yPlugin);
        _.extend(config, {
          plugins: {
            a11y: { i18n: this.i18n },
            pagination: {
              pageSizeText: this.i18n.getString('Grid_ShowRows'),
              pageNumberLabelText: this.i18n.getString('GridPage_Colon'),
              totalPageCountText: this.i18n.getString('Grid_PageOf'),
            },
          },
        });
      }

      this.grid = gridFactory
        .create(_.extend({}, config, {
          events: {
            load: (e) => {
              this.handleBidAdjustmentChange(e);
              this.handleCashbackPercentChange(e);
            },
            'change .bid-adjustment-input': e => this.handleBidAdjustmentChange(e),
            'change select.bid-adjustment-select': e => this.handleBidAdjustmentChange(e),
            // 'input .cashback-percent-input': e => this.handleCashbackPercentChange(e),
            // 'change select.cashback-option-select': e => this.handleCashbackOptionChange(e),
          },
        }));

      this.$('.bid-adjustment-grid').append(this.grid.gridView.render().el);
    }
  }
}
